import bridge from '@/plugins/vue-constructor-bridge';

/**
 * Clover 3DS is a server-driven "payment action" flow (unlike Paay/Cardinal, which produce
 * a pre-payment token). The Create-a-Charge response tells the client which flow to run via
 * paymentAction.action; the Clover SDK runs it in an iframe and reports completion through a
 * document-level `executePatch` CustomEvent carrying detail._3DSStatus.
 * https://docs.clover.com/dev/docs/configure-3d-secure-sdk-with-iframe-and-api-integration
 */

export const CLOVER_PAYMENT_ACTION_TYPE = 'Clover3ds';

export const CLOVER_ACTION = {
  METHOD_FLOW: 'THREEDS_METHOD_FLOW_REQUIRED',
  CHALLENGE_FLOW: 'CHALLENGE_FLOW_REQUIRED'
};

export const CLOVER_STATUS = {
  METHOD_FLOW_COMPLETED: 'THREEDS_METHOD_FLOW_COMPLETED',
  CHALLENGE_FLOW_COMPLETED: 'CHALLENGE_FLOW_COMPLETED'
};

const SUCCESS_STATUSES = [
  CLOVER_STATUS.METHOD_FLOW_COMPLETED,
  CLOVER_STATUS.CHALLENGE_FLOW_COMPLETED
];

export const mapBrowserInfo = (raw) => {
  if (!raw) return null;

  return {
    browserAcceptHeader: raw.browser_accept_header,
    browserHttpOrigin: raw.browser_http_origin,
    browserJavaEnabled: raw.browser_java_enabled,
    browserJavascriptEnabled: raw.browser_javascript_enabled,
    browserLanguage: raw.browser_language,
    browserColorDepth: raw.browser_color_depth,
    browserScreenHeight: raw.browser_screen_height,
    browserScreenWidth: raw.browser_screen_width,
    browserTimeZoneOffset: raw.browser_tz,
    browserUserAgent: raw.browser_user_agent
  };
};

/**
 * SDK is a singleton — reuse the instance across payment attempts, but rebuild it when the merchant
 * changes (the shared SPA can move between locations/merchants, each with its own merchantUuid).
 */
export const initCloverTds = ({ merchantUuid }) => {
  if (!window.Clover3DS) {
    bridge.triggerNewRelicEvent('Clover3dsSdkInitialization', 'Error', 'Clover 3DS SDK not loaded on page');

    return null;
  }

  if (!window._3DSUtil || window._3DSActiveMerchantUuid !== merchantUuid) {
    window._3DSUtil = new window.Clover3DS({ merchantUuid });
    window._3DSActiveMerchantUuid = merchantUuid;

    bridge.triggerNewRelicEvent('Clover3dsSdkInitialization', 'Info', 'Clover 3DS SDK initialized');
  }

  return window._3DSUtil;
};

export const getBrowserInfo = () => {
  if (!window._3DSUtil?.getBrowserInfo) {
    bridge.triggerNewRelicEvent('Clover3dsBrowserInfo', 'Error', 'Clover 3DS SDK unavailable for browser info');

    return null;
  }

  const browserInfo = mapBrowserInfo(window._3DSUtil.getBrowserInfo());

  bridge.triggerNewRelicEvent('Clover3dsBrowserInfo', 'Info', 'Browser info collected');

  return browserInfo;
};

/** Maps the method-flow action data to the SDK's perform3DSFingerPrinting options. */
export const buildFingerPrintingOptions = actionData => ({
  threeDSServerTransID: actionData.threedsServerTransactionId,
  threeDSMethodUrl: actionData.methodUrl,
  methodNotificationUrl: actionData.methodNotificationUrl
});

export const buildChallengeOptions = actionData => ({
  acsUrl: actionData.acsUrl,
  acsTransID: actionData.acsTransactionId,
  threeDSServerTransID: actionData.threedsServerTransactionId,
  messageVersion: actionData.threedsProtocolVersion
});

export const buildCloverConfirmBody = ({ orderPaymentId, status }) => ({
  paymentType: 'CreditCard',
  paymentAction: {
    type: 'Confirm',
    orderPaymentId
  },
  supportsPaymentActions: true,
  threeds: { status }
});

export const handlePaymentAction = paymentAction => new Promise((resolve, reject) => {
  const cloverTds = window._3DSUtil;
  const { action } = paymentAction;
  const actionData = paymentAction?.data?.data;

  if (!cloverTds || !actionData) {
    bridge.triggerNewRelicEvent('Clover3dsActionError', 'Error', 'Clover 3DS unavailable or missing action data', { action });

    reject(new Error('Clover 3DS action could not be started'));

    return;
  }

  const onExecutePatch = (event) => {
    const status = event.detail?._3DSStatus;

    /**
     * The method (fingerprinting) flow is best-effort: EMV 3DS treats a failed or timed-out 3DS Method
     * as non-fatal — the issuer still resolves the authentication (frictionless or challenge) from the
     * charge data. Clover's contract is to resubmit whatever flow_status the SDK reports and let the
     * server decide the next action, so any method-flow status resubmits a Confirm. Only the challenge
     * flow is the real authentication step, so a non-completed status there is terminal.
     */
    if (SUCCESS_STATUSES.includes(status)) {
      bridge.triggerNewRelicEvent('Clover3dsActionCompleted', 'Info', 'Clover 3DS action completed', { action, status });

      resolve(status);
    }
    else if (action === CLOVER_ACTION.METHOD_FLOW) {
      bridge.triggerNewRelicEvent('Clover3dsMethodFlowNonFatal', 'Info', 'Clover 3DS method flow did not complete; resubmitting status', { action, status });

      resolve(status);
    }
    else {
      bridge.triggerNewRelicEvent('Clover3dsActionFailed', 'Error', 'Clover 3DS action failed', { action, status });

      reject(new Error(status || 'Clover 3DS action failed'));
    }
  };

  document.addEventListener('executePatch', onExecutePatch, { once: true });

  try {
    if (action === CLOVER_ACTION.METHOD_FLOW) {
      const fingerPrintingOptions = buildFingerPrintingOptions(actionData);

      bridge.triggerNewRelicEvent('Clover3dsMethodFlowInitiated', 'Info', 'Clover 3DS method flow initiated');

      cloverTds.perform3DSFingerPrinting(fingerPrintingOptions);
    }
    else if (action === CLOVER_ACTION.CHALLENGE_FLOW) {
      const challengeOptions = buildChallengeOptions(actionData);

      bridge.triggerNewRelicEvent('Clover3dsChallengeInitiated', 'Info', 'Clover 3DS challenge flow initiated');

      cloverTds.perform3DSChallenge(challengeOptions);
    }
    else {
      document.removeEventListener('executePatch', onExecutePatch);

      bridge.triggerNewRelicEvent('Clover3dsActionError', 'Error', 'Unknown Clover 3DS action', { action });

      reject(new Error(`Unknown Clover 3DS action: ${action}`));
    }
  }
  catch (error) {
    document.removeEventListener('executePatch', onExecutePatch);

    bridge.triggerNewRelicEvent('Clover3dsActionError', 'Error', 'Clover 3DS SDK threw during action', { action, errorMessage: error.message });

    reject(error);
  }
});
